/**
 * Plot & Identify Ordered Pairs generator (6.MGSR.3.1).
 *
 * Read a plotted point in any of the four quadrants: its x-coordinate, its
 * y-coordinate, which quadrant it lies in, or its full ordered pair. The point
 * is chosen first (integer coordinates, off both axes) and a CoordinateModel is
 * attached showing it, so the drawn point always matches the graded answer.
 *
 * Families:
 *   F1: identify the x-coordinate (number)
 *   F2: identify the y-coordinate (number)
 *   F3: which quadrant (choice: I / II / III / IV)
 *   F4: identify the ordered pair (choice of pairs)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type { CoordinateModel, CoordinatePoint } from "../graph/plot-model";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "coordinate-plane" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 6 },
  standard: { families: ["F1", "F2", "F3"], max: 8 },
  hard: { families: ["F1", "F2", "F3", "F4"], max: 10 },
  challenge: { families: ["F3", "F4"], max: 10 },
};

/** Quadrant of a point with nonzero integer coordinates. */
function quadrant(x: number, y: number): "I" | "II" | "III" | "IV" {
  if (x > 0 && y > 0) return "I";
  if (x < 0 && y > 0) return "II";
  if (x < 0 && y < 0) return "III";
  return "IV";
}

function pairLabel(x: number, y: number): string {
  return `(${x}, ${y})`;
}

export class CoordinatePlaneGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      // Nonzero integer coordinates so the point sits inside a quadrant.
      const x = rng.intNonZero(-cfg.max, cfg.max);
      const y = rng.intNonZero(-cfg.max, cfg.max);
      const point: CoordinatePoint = { x: rat(x), y: rat(y), closed: true, label: "A" };
      const win = cfg.max + 1;
      const graph: CoordinateModel = {
        kind: "coordinate",
        xMin: -win,
        xMax: win,
        yMin: -win,
        yMax: win,
        points: [point],
        segments: [],
      };

      const base = {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        subskills: topic.subskills,
        graph,
      } as const;

      if (family === "F1" || family === "F2") {
        const isX = family === "F1";
        const value = rat(isX ? x : y);
        return {
          ...base,
          prompt: {
            text: `Point A is plotted at ${pairLabel(x, y)}. What is its ${isX ? "x" : "y"}-coordinate?`,
          },
          answerType: "number",
          answer: { kind: "number", value },
          steps: [
            {
              description: isX
                ? "The x-coordinate is the first number in the ordered pair (horizontal position)."
                : "The y-coordinate is the second number in the ordered pair (vertical position).",
              latex: value.toLatex(),
            },
          ],
          hints: [
            { level: 1, text: "An ordered pair is written as (x, y)." },
            {
              level: 2,
              text: isX
                ? "The x-coordinate tells how far left or right of the origin the point is."
                : "The y-coordinate tells how far up or down from the origin the point is.",
            },
          ],
        };
      }

      if (family === "F3") {
        const correctId = quadrant(x, y);
        const choices: Choice[] = [
          { id: "I", label: "Quadrant I" },
          { id: "II", label: "Quadrant II" },
          { id: "III", label: "Quadrant III" },
          { id: "IV", label: "Quadrant IV" },
        ];
        return {
          ...base,
          prompt: { text: `In which quadrant does the point ${pairLabel(x, y)} lie?` },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description:
                "Quadrant I is (+,+), II is (-,+), III is (-,-), and IV is (+,-). Match the signs of the coordinates.",
              latex: `(${x}, ${y})`,
            },
          ],
          hints: [
            { level: 1, text: "Look at the sign of each coordinate." },
            { level: 2, text: "Quadrants are numbered counter-clockwise starting from the top right." },
          ],
          choices,
        };
      }

      // F4: identify the ordered pair (choice of pairs).
      const distractors = new Set<string>();
      const correctLabel = pairLabel(x, y);
      const candidatePairs: [number, number][] = [
        [y, x], // swapped
        [-x, y],
        [x, -y],
        [-x, -y],
      ];
      const wrong: [number, number][] = [];
      for (const [cx, cy] of candidatePairs) {
        const lbl = pairLabel(cx, cy);
        if (lbl === correctLabel || distractors.has(lbl)) continue;
        distractors.add(lbl);
        wrong.push([cx, cy]);
        if (wrong.length === 3) break;
      }
      if (wrong.length < 2) continue;
      const options = [
        { id: "c0", label: correctLabel, correct: true },
        ...wrong.map((p, i) => ({ id: `c${i + 1}`, label: pairLabel(p[0], p[1]), correct: false })),
      ];
      const shuffled = rng.shuffle(options);
      const choices: Choice[] = shuffled.map((o) => ({ id: o.id, label: o.label }));
      const correct = shuffled.find((o) => o.correct);
      if (!correct) continue;
      return {
        ...base,
        prompt: { text: `What are the coordinates of point A shown on the grid?` },
        answerType: "choice",
        answer: { kind: "choice", value: correct.id },
        steps: [
          {
            description:
              "Read the horizontal position (x) first, then the vertical position (y): (x, y).",
            latex: `(${x}, ${y})`,
          },
        ],
        hints: [
          { level: 1, text: "Count across for x, then up or down for y." },
          { level: 2, text: "The order matters: (x, y), not (y, x)." },
        ],
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "choice"
        ? q.answer.value
        : q.answer.kind === "number"
          ? q.answer.value.key()
          : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const coordinatePlane = new CoordinatePlaneGenerator();
